import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useState } from 'react';
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '../context/ThemeContext';
import { useStoryProgress } from '../context/StoryProgressContext';
import { getThemeColors } from '../utils/themeStyles';
import { getTaskData } from '../data/mockTaskData';
import { getScenarioForMission } from '../data/scenarios';

export default function TaskDetailScreen({ navigation: navProp, route: routeProp }: { navigation?: any; route?: any } = {}) {
  const navigation = navProp ?? useNavigation<any>();
  const route = routeProp ?? useRoute<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);

  const { missionId, chapterId, itemId, title: routeTitle } = route.params ?? {};
  const { completeItem } = useStoryProgress();
  const scenario = missionId ? getScenarioForMission(missionId) : undefined;
  const scenarioChapter = scenario?.chapters?.[(chapterId ?? 1) - 1] ?? scenario?.chapters?.[0];
  // Recherche par id d'item (blob string), repli sur l'index pour le legacy numérique.
  const sequenceItem = itemId != null
    ? (scenarioChapter?.sequence?.find(s => String(s.id) === String(itemId))
        ?? scenarioChapter?.sequence?.[Number(itemId) - 1])
    : undefined;
  const scenarioTask =
    sequenceItem?.type === 'task' &&
    typeof sequenceItem.content === 'object' &&
    'steps' in sequenceItem.content &&
    'objective' in sequenceItem.content
      ? sequenceItem
      : undefined;

  const task = scenarioTask
    ? {
        itemId: itemId ?? 0,
        title: scenarioTask.title,
        subtitle: scenario?.certification ? `${scenario.certification} — Task` : 'Task',
        points: 20,
        priority: 'High' as const,
        difficulty: 'Moyen' as const,
        estimatedTime: `${scenarioTask.duration ?? 20} min`,
        objective: String((scenarioTask.content as any).objective ?? ''),
        context: scenario?.title ?? '',
        steps: ((scenarioTask.content as any).steps ?? []).map((s: any, idx: number) => ({
          number: Number(s.stepNumber ?? idx + 1),
          title: String(s.action ?? `Etape ${idx + 1}`),
          detail: String(s.detail ?? ''),
        })),
        criteria: [{ id: 1, text: String((scenarioTask.content as any).validation ?? 'Validation de la tache') }],
        resources: [],
      }
    : getTaskData(itemId);

  const [checkedCriteria, setCheckedCriteria] = useState<Set<number>>(new Set());
  const [isDone, setIsDone] = useState(false);

  const toggleCriteria = (id: number) => {
    setCheckedCriteria(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const allChecked = task ? checkedCriteria.size >= task.criteria.length : false;

  const getDifficultyColor = (d: string) => {
    if (d === 'Facile') return '#10B981';
    if (d === 'Moyen') return '#F59E0B';
    return '#EF4444';
  };

  const displayTitle = task?.title ?? routeTitle ?? 'Tache';

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
        </TouchableOpacity>
        <View style={styles.headerInfo}>
          <Text style={[styles.headerTitle, { color: colors.textPrimary }]} numberOfLines={1}>
            {displayTitle}
          </Text>
          {task?.subtitle ? (
            <Text style={[styles.headerSub, { color: colors.textSecondary }]}>{task.subtitle}</Text>
          ) : null}
        </View>
        <View style={[styles.pointsBadge, { backgroundColor: '#EF4444' + '22' }]}>
          <Text style={[styles.pointsText, { color: '#EF4444' }]}>+{task?.points ?? 0} XP</Text>
        </View>
      </View>

      <ScrollView style={styles.scroll} showsVerticalScrollIndicator={false}>
        <View style={styles.content}>

          {/* Meta badges */}
          <View style={styles.metaRow}>
            {task ? (
              <>
                <View style={[styles.metaBadge, { backgroundColor: getDifficultyColor(task.difficulty) + '22' }]}>
                  <Text style={[styles.metaBadgeText, { color: getDifficultyColor(task.difficulty) }]}>{task.difficulty}</Text>
                </View>
                <View style={[styles.metaBadge, { backgroundColor: colors.surfaceSecondary }]}>
                  <Ionicons name="time-outline" size={12} color={colors.textSecondary} />
                  <Text style={[styles.metaBadgeText, { color: colors.textSecondary }]}>{task.estimatedTime}</Text>
                </View>
              </>
            ) : null}
            {isDone && (
              <View style={[styles.metaBadge, { backgroundColor: '#10B981' + '22' }]}>
                <Ionicons name="checkmark-circle" size={12} color="#10B981" />
                <Text style={[styles.metaBadgeText, { color: '#10B981' }]}>Termine</Text>
              </View>
            )}
          </View>

          {/* Context banner */}
          {task?.context ? (
            <View style={[styles.contextCard, { backgroundColor: isDarkTheme ? '#1E3A5F' : '#EFF6FF', borderColor: isDarkTheme ? '#2563EB44' : '#BFDBFE' }]}>
              <Ionicons name="information-circle" size={16} color="#3B82F6" />
              <Text style={[styles.contextText, { color: isDarkTheme ? '#93C5FD' : '#1D4ED8' }]}>{task.context}</Text>
            </View>
          ) : null}

          {/* Objective */}
          <View style={[styles.card, { backgroundColor: colors.cardBackground }]}>
            <View style={styles.cardHeader}>
              <Ionicons name="flag" size={18} color="#EF4444" />
              <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>Objectif</Text>
            </View>
            <Text style={[styles.cardBody, { color: colors.textSecondary }]}>{task?.objective ?? displayTitle}</Text>
          </View>

          {/* Steps */}
          {task?.steps && task.steps.length > 0 ? (
            <View style={[styles.card, { backgroundColor: colors.cardBackground }]}>
              <View style={styles.cardHeader}>
                <Ionicons name="list" size={18} color="#3B82F6" />
                <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>Etapes</Text>
              </View>
              {task.steps.map((step: any) => (
                <View key={step.number} style={styles.stepRow}>
                  <View style={[styles.stepNum, { backgroundColor: '#3B82F6' }]}>
                    <Text style={styles.stepNumText}>{step.number}</Text>
                  </View>
                  <View style={styles.stepContent}>
                    <Text style={[styles.stepTitle, { color: colors.textPrimary }]}>{step.title}</Text>
                    <Text style={[styles.stepDetail, { color: colors.textSecondary }]}>{step.detail}</Text>
                  </View>
                </View>
              ))}
            </View>
          ) : null}

          {/* Acceptance criteria */}
          {task?.criteria && task.criteria.length > 0 ? (
            <View style={[styles.card, { backgroundColor: colors.cardBackground }]}>
              <View style={styles.cardHeader}>
                <Ionicons name="checkmark-done" size={18} color="#10B981" />
                <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>
                  Criteres d'acceptation ({checkedCriteria.size}/{task.criteria.length})
                </Text>
              </View>
              {task.criteria.map(c => {
                const checked = checkedCriteria.has(c.id);
                return (
                  <TouchableOpacity key={c.id} style={styles.criteriaRow} onPress={() => toggleCriteria(c.id)}>
                    <View style={[styles.checkBox, {
                      borderColor: checked ? '#10B981' : colors.border,
                      backgroundColor: checked ? '#10B981' : 'transparent',
                    }]}>
                      {checked && <Ionicons name="checkmark" size={12} color="#FFF" />}
                    </View>
                    <Text style={[styles.criteriaText, { color: checked ? colors.textSecondary : colors.textPrimary }, checked && styles.strikethrough]}>
                      {c.text}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          ) : null}

          {/* Resources */}
          {task?.resources && task.resources.length > 0 ? (
            <View style={[styles.card, { backgroundColor: colors.cardBackground }]}>
              <View style={styles.cardHeader}>
                <Ionicons name="book" size={18} color="#8B5CF6" />
                <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>Ressources</Text>
              </View>
              {task.resources.map((r, i) => (
                <View key={i} style={[styles.resourceRow, { borderColor: colors.border }]}>
                  <Ionicons
                    name={r.type === 'video' ? 'play-circle-outline' : r.type === 'lab' ? 'flask-outline' : 'document-text-outline'}
                    size={18}
                    color="#8B5CF6"
                  />
                  <Text style={[styles.resourceTitle, { color: colors.primary }]}>{r.title}</Text>
                  <View style={[styles.resourceTypeBadge, { backgroundColor: '#8B5CF6' + '22' }]}>
                    <Text style={[styles.resourceTypeText, { color: '#8B5CF6' }]}>{r.type}</Text>
                  </View>
                </View>
              ))}
            </View>
          ) : null}

          {/* Mark done button */}
          <TouchableOpacity
            style={[
              styles.doneBtn,
              {
                backgroundColor: isDone ? '#10B981' : allChecked ? '#10B981' : colors.surfaceSecondary,
                opacity: isDone || allChecked ? 1 : 0.6,
              },
            ]}
            onPress={() => { if (allChecked || !task) { setIsDone(true); if (itemId != null && missionId != null) void completeItem(missionId, String(itemId)); } }}
          >
            <Ionicons name={isDone ? 'checkmark-circle' : 'checkmark-circle-outline'} size={20} color={isDone || allChecked ? '#FFF' : colors.textTertiary} />
            <Text style={[styles.doneBtnText, { color: isDone || allChecked ? '#FFF' : colors.textTertiary }]}>
              {isDone ? 'Tache terminee !' : allChecked ? 'Marquer comme terminee' : `Cochez les criteres (${checkedCriteria.size}/${task?.criteria.length ?? 0})`}
            </Text>
          </TouchableOpacity>

        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, gap: 10 },
  backBtn: { padding: 6 },
  headerInfo: { flex: 1 },
  headerTitle: { fontSize: 16, fontWeight: '700' },
  headerSub: { fontSize: 12, marginTop: 2 },
  pointsBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 10 },
  pointsText: { fontSize: 13, fontWeight: '700' },
  scroll: { flex: 1 },
  content: { padding: 16 },
  metaRow: { flexDirection: 'row', gap: 8, marginBottom: 14, flexWrap: 'wrap' },
  metaBadge: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 10 },
  metaBadgeText: { fontSize: 12, fontWeight: '600' },
  contextCard: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, borderWidth: 1, borderRadius: 10, padding: 12, marginBottom: 14 },
  contextText: { flex: 1, fontSize: 13, lineHeight: 19, fontStyle: 'italic' },
  card: { borderRadius: 12, padding: 16, marginBottom: 14, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.08, shadowRadius: 4, elevation: 2 },
  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 },
  cardTitle: { fontSize: 15, fontWeight: '700' },
  cardBody: { fontSize: 14, lineHeight: 21 },
  stepRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginBottom: 14 },
  stepNum: { width: 26, height: 26, borderRadius: 13, alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 },
  stepNumText: { color: '#FFF', fontSize: 13, fontWeight: '700' },
  stepContent: { flex: 1 },
  stepTitle: { fontSize: 14, fontWeight: '600', marginBottom: 3 },
  stepDetail: { fontSize: 13, lineHeight: 19 },
  criteriaRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginBottom: 10 },
  checkBox: { width: 20, height: 20, borderRadius: 4, borderWidth: 2, alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 },
  criteriaText: { flex: 1, fontSize: 13, lineHeight: 19 },
  strikethrough: { textDecorationLine: 'line-through', opacity: 0.6 },
  resourceRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 8, borderBottomWidth: 1 },
  resourceTitle: { flex: 1, fontSize: 13, fontWeight: '500' },
  resourceTypeBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 },
  resourceTypeText: { fontSize: 11, fontWeight: '600' },
  doneBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 14, borderRadius: 12, marginTop: 8, marginBottom: 24 },
  doneBtnText: { fontSize: 15, fontWeight: '700' },
});

